import { useState, useCallback, useEffect, useRef } from 'react';
import { Plus } from 'lucide-react';
import { APP } from './tokens';

export interface SendForSignatureData {
  signerEmails: string[];
  /** Lowercased email → display name for invites/API `signers[].name` (optional). */
  signerDisplayNamesByEmail?: Record<string, string>;
  ccEmails: string[];
  signerRole: string;
  message?: string;
  expiresInDays?: number;
  requireVerification: boolean;
}

interface SendForSignatureModalProps {
  open: boolean;
  onClose: () => void;
  fromDisplayName: string;
  fromEmail: string;
  documentName: string;
  pageCount: number;
  fieldCount: number;
  initialSignerEmails?: string[];
  onSend: (data: SendForSignatureData) => void;
  sending?: boolean;
}

/** Applied to every recipient for BoldSign-style invites (role chips removed from UI). */
const DEFAULT_SIGNER_ROLE = 'Signer';
const MAX_SIGNERS = 10;

/** Same loose check as ChipRow commit — pending text in the input counts as a signer. */
function isPendingSignerInput(value: string, committed: string[]): boolean {
  const email = value.trim();
  const normalized = email.toLowerCase();
  return Boolean(email && email.includes('@') && !committed.some((existing) => existing.toLowerCase() === normalized));
}

function guessNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  const cleaned = local.replace(/[._-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!cleaned) return 'Signer';
  return cleaned
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .filter(Boolean)
    .join(' ');
}

function fieldLabelStyle(): React.CSSProperties {
  return {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: APP.textMuted,
    lineHeight: '12px',
  };
}

function ChipRow({
  label,
  emails,
  inputValue,
  onInputChange,
  onCommit,
  onRemove,
  placeholderEmpty,
  placeholderMore,
  inputRef,
}: {
  label: string;
  emails: string[];
  inputValue: string;
  onInputChange: (v: string) => void;
  onCommit: () => void;
  onRemove: (email: string) => void;
  placeholderEmpty: string;
  placeholderMore: string;
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={fieldLabelStyle()}>{label}</span>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 4,
          borderRadius: 6,
          paddingBlock: 5,
          paddingInline: 8,
          backgroundColor: APP.bgSearchBar,
          border: `1px solid ${APP.border}`,
          minHeight: 34,
        }}
      >
        {emails.map((email) => (
          <span
            key={email}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              borderRadius: 4,
              paddingBlock: 2,
              paddingInline: 6,
              backgroundColor: '#D97645',
              fontSize: 12,
              fontWeight: 500,
              color: '#FFFFFF',
              fontFamily: APP.fontFamily,
            }}
          >
            {email}
            <button
              type="button"
              onClick={() => onRemove(email)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}
            >
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                <path d="M2 2L6 6M6 2L2 6" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="email"
          className="send-sig-modal__chip-input"
          placeholder={emails.length === 0 ? placeholderEmpty : placeholderMore}
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              onCommit();
            }
          }}
          onBlur={onCommit}
          style={{
            flex: 1,
            minWidth: 60,
            border: 'none',
            background: 'none',
            outline: 'none',
            fontSize: 12,
            color: APP.textPrimary,
            fontFamily: APP.fontFamily,
          }}
        />
      </div>
    </div>
  );
}

export default function SendForSignatureModal({
  open,
  onClose,
  fromDisplayName,
  fromEmail,
  documentName,
  pageCount,
  fieldCount,
  initialSignerEmails = [],
  onSend,
  sending,
}: SendForSignatureModalProps) {
  const [toEmails, setToEmails] = useState<string[]>(initialSignerEmails);
  const [toInput, setToInput] = useState('');
  const [signerDisplayNamesByEmail, setSignerDisplayNamesByEmail] = useState<Record<string, string>>({});
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState('');
  const [message, setMessage] = useState('');
  const [expires, setExpires] = useState(true);
  const [requireVerification, setRequireVerification] = useState(true);
  const toInputRef = useRef<HTMLInputElement | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setToEmails(initialSignerEmails);
      setToInput('');
      setSignerDisplayNamesByEmail({});
      setCcEmails([]);
      setCcInput('');
      setMessage('');
      setExpires(true);
      setRequireVerification(true);
    }
    wasOpenRef.current = open;
  }, [open, initialSignerEmails]);

  const addTo = useCallback(() => {
    const email = toInput.trim();
    const normalized = email.toLowerCase();
    if (email && email.includes('@') && toEmails.length < MAX_SIGNERS && !toEmails.some((existing) => existing.toLowerCase() === normalized)) {
      const key = email.toLowerCase();
      setToEmails((prev) => [...prev, email]);
      setSignerDisplayNamesByEmail((prev) => (key in prev ? prev : { ...prev, [key]: '' }));
      setToInput('');
    }
  }, [toInput, toEmails]);

  const focusSignerInput = useCallback(() => {
    addTo();
    window.requestAnimationFrame(() => toInputRef.current?.focus());
  }, [addTo]);

  const addCc = useCallback(() => {
    const email = ccInput.trim();
    if (email && email.includes('@') && !ccEmails.includes(email)) {
      setCcEmails((prev) => [...prev, email]);
      setCcInput('');
    }
  }, [ccInput, ccEmails]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const signerEmailsForAction =
    isPendingSignerInput(toInput, toEmails) && toEmails.length < MAX_SIGNERS ? [...toEmails, toInput.trim()] : toEmails;

  const canSend = signerEmailsForAction.length > 0 && signerEmailsForAction.length <= MAX_SIGNERS && !sending;

  const handleSend = useCallback(() => {
    const signers =
      isPendingSignerInput(toInput, toEmails) && toEmails.length < MAX_SIGNERS ? [...toEmails, toInput.trim()] : toEmails;
    if (signers.length === 0 || signers.length > MAX_SIGNERS || sending) return;
    onSend({
      signerEmails: signers,
      signerDisplayNamesByEmail,
      ccEmails,
      signerRole: DEFAULT_SIGNER_ROLE,
      message: message.trim() || undefined,
      expiresInDays: expires ? 7 : undefined,
      requireVerification,
    });
  }, [toInput, toEmails, sending, ccEmails, message, expires, requireVerification, onSend, signerDisplayNamesByEmail]);

  if (!open) return null;

  const inputStyle: React.CSSProperties = {
    border: `1px solid ${APP.border}`,
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    fontFamily: APP.fontFamily,
    color: APP.textPrimary,
    backgroundColor: APP.bgChatInput,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    lineHeight: '16px',
  };

  const senderBadge = (
    <span
      style={{
        borderRadius: 999,
        paddingBlock: 3,
        paddingInline: 8,
        backgroundColor: APP.bgSearchBar,
        border: `1px solid ${APP.border}`,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase' as const,
        color: APP.textMuted,
        fontFamily: APP.fontFamily,
        lineHeight: '12px',
      }}
    >
      Sender
    </span>
  );

  const fromBlock = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
        <span style={fieldLabelStyle()}>From</span>
        {senderBadge}
      </div>
      <div
        style={{
          ...inputStyle,
          color: APP.textPrimary,
          cursor: 'default',
        }}
      >
        <span style={{ fontWeight: 500 }}>{fromDisplayName}</span>
        {fromEmail ? (
          <span style={{ color: APP.textMuted }}>{` · ${fromEmail}`}</span>
        ) : null}
      </div>
    </div>
  );

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        fontFamily: APP.fontFamily,
      }}
    >
      <style>{`
        .send-sig-modal__chip-input::placeholder,
        .send-sig-modal__field::placeholder {
          color: var(--app-text-muted);
          opacity: 1;
        }
      `}</style>
      <div
        className="send-sig-modal"
        style={{
          width: 480,
          maxWidth: 'calc(100vw - 32px)',
          backgroundColor: APP.bgCardBody,
          borderRadius: 16,
          border: `1px solid ${APP.border}`,
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'clip',
          color: APP.textPrimary,
        }}
      >
        <div
          style={{
            paddingBlock: 20,
            paddingInline: 24,
            borderBottom: `1px solid ${APP.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="1.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
              <path d="M22 2L11 13" />
              <path d="M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
            <span style={{ fontSize: 16, fontWeight: 600, color: APP.textPrimary, lineHeight: '20px', fontFamily: APP.fontFamily }}>
              Send for Signature
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              color: APP.textMuted,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            paddingBlock: 16,
            paddingInline: 24,
            gap: 12,
            backgroundColor: APP.bgDocColumn,
            borderBottom: `1px solid ${APP.border}`,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="1.5" style={{ flexShrink: 0 }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
            <path d="M14 2v6h6" />
          </svg>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: APP.textPrimary, lineHeight: '16px', fontFamily: APP.fontFamily }}>
              {documentName}
            </span>
            <span style={{ fontSize: 11, fontWeight: 400, color: APP.textMuted, lineHeight: '14px', fontFamily: APP.fontFamily }}>
              {pageCount} page{pageCount !== 1 ? 's' : ''} · {fieldCount} signature field{fieldCount !== 1 ? 's' : ''} for recipient(s)
            </span>
          </div>
        </div>

        <div style={{ paddingBlock: 20, paddingInline: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {fromBlock}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <ChipRow
              label="Signers"
              emails={toEmails}
              inputValue={toInput}
              onInputChange={setToInput}
              onCommit={addTo}
              onRemove={(e) => {
                const key = e.trim().toLowerCase();
                setToEmails((prev) => prev.filter((x) => x !== e));
                setSignerDisplayNamesByEmail((prev) => {
                  const next = { ...prev };
                  delete next[key];
                  return next;
                });
              }}
              placeholderEmpty="Add signer email…"
              placeholderMore="Add another…"
              inputRef={toInputRef}
            />

            <button
              type="button"
              onClick={focusSignerInput}
              disabled={toEmails.length >= MAX_SIGNERS}
              style={{
                alignSelf: 'flex-start',
                border: `1px solid ${APP.border}`,
                borderRadius: 6,
                paddingBlock: 6,
                paddingInline: 10,
                backgroundColor: APP.bgSearchBar,
                color: toEmails.length >= MAX_SIGNERS ? APP.textMuted : APP.textPrimary,
                fontSize: 12,
                fontWeight: 600,
                cursor: toEmails.length >= MAX_SIGNERS ? 'default' : 'pointer',
                fontFamily: APP.fontFamily,
                lineHeight: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                opacity: toEmails.length >= MAX_SIGNERS ? 0.65 : 1,
              }}
            >
              <Plus size={13} strokeWidth={2} />
              Add signer
            </button>
          </div>

          {signerEmailsForAction.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span style={fieldLabelStyle()}>Signer names</span>
              <span style={{ fontSize: 11, fontWeight: 400, color: APP.textSecondary, lineHeight: '14px', fontFamily: APP.fontFamily }}>
                Used on invitation emails and as the API signer name. For a single recipient, we also replace detected recipient printed-name placeholders on the PDF when the document was analyzed earlier.
              </span>
              {signerEmailsForAction.map((email) => {
                const key = email.trim().toLowerCase();
                return (
                  <div key={email} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 500, color: APP.textMuted, fontFamily: APP.fontFamily }}>
                      {email}
                    </span>
                    <input
                      type="text"
                      className="send-sig-modal__field"
                      placeholder={guessNameFromEmail(email)}
                      value={signerDisplayNamesByEmail[key] ?? ''}
                      onChange={(e) =>
                        setSignerDisplayNamesByEmail((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                      style={inputStyle}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {fieldCount === 0 && (
            <span style={{ fontSize: 12, fontWeight: 400, color: APP.textSecondary, lineHeight: '16px', fontFamily: APP.fontFamily }}>
              Optional: place recipient signature fields on the document so signers see exactly where to sign. You can send the invite without them—they&apos;ll apply their signature from the signing link.
            </span>
          )}

          <ChipRow
            label="Cc"
            emails={ccEmails}
            inputValue={ccInput}
            onInputChange={setCcInput}
            onCommit={addCc}
            onRemove={(e) => setCcEmails((prev) => prev.filter((x) => x !== e))}
            placeholderEmpty="Cc (optional)…"
            placeholderMore="Add another…"
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: APP.textPrimary, lineHeight: '16px', fontFamily: APP.fontFamily }}>
              Message (optional)
            </span>
            <textarea
              className="send-sig-modal__field"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Please review and sign…"
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 64 }}
            />
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTop: `1px solid ${APP.border}`,
              paddingBlock: 12,
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={expires} onChange={(e) => setExpires(e.target.checked)} style={{ display: 'none' }} />
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, color: APP.textMuted }}>
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              <span style={{ fontSize: 12, fontWeight: 400, color: APP.textSecondary, lineHeight: '16px', fontFamily: APP.fontFamily }}>
                Expires in 7 days
              </span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={requireVerification} onChange={(e) => setRequireVerification(e.target.checked)} style={{ display: 'none' }} />
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, color: APP.textMuted }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span style={{ fontSize: 12, fontWeight: 400, color: APP.textSecondary, lineHeight: '16px', fontFamily: APP.fontFamily }}>
                Email verification required
              </span>
            </label>
          </div>
        </div>

        <div
          style={{
            paddingBlock: 16,
            paddingInline: 24,
            backgroundColor: APP.bgDocColumn,
            borderTop: `1px solid ${APP.border}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              border: `1px solid ${APP.border}`,
              borderRadius: 8,
              padding: '9px 20px',
              backgroundColor: 'transparent',
              color: APP.textPrimary,
              fontSize: 13,
              fontWeight: 400,
              cursor: 'pointer',
              fontFamily: APP.fontFamily,
              lineHeight: '16px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '9px 24px',
              backgroundColor: canSend ? '#D97645' : APP.bgSubmitBtn,
              color: '#FFFFFF',
              fontSize: 13,
              fontWeight: 500,
              cursor: canSend ? 'pointer' : 'default',
              fontFamily: APP.fontFamily,
              lineHeight: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              opacity: canSend ? 1 : 0.7,
            }}
          >
            {sending ? 'Sending…' : (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
                  <path d="M22 2L11 13" />
                  <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
                Send
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
